import type { SupSingleVaultV3, SupStoredFilterRangeValue } from "@/types";
import React, { useCallback, useMemo } from "react";

import dictionary from "@/../config/dictionary.config";
import { useStoredData, useStoredDataDispatch } from "@/store";

import GridBarFiltersAtomRange from "@/components/Grid/Bar/Filters/Atom/Range";
import GridBarFiltersAtomHeader from "@/components/Grid/Bar/Filters/Atom/Header";

interface GridBarFiltersSharpeRangeProps {
  vaults?: SupSingleVaultV3[];
}

export default function GridBarFiltersSharpeRange({ vaults }: GridBarFiltersSharpeRangeProps) {
  const { min, max } = useMemo(() => {
    return (
      vaults
        ?.filter(vault => vault.vault_statistics.displaySharpe !== dictionary.statsDefaultValue)
        .reduce(
          (acc, vault) => {
            return {
              min: Math.min(acc.min, parseFloat(vault.vault_statistics.displaySharpe.toFixed(2))),
              max: Math.max(acc.max, parseFloat(vault.vault_statistics.displaySharpe.toFixed(2))),
            };
          },
          { min: Infinity, max: -Infinity }
        ) ?? { min: 0, max: 100 }
    );
  }, [vaults]);

  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const filterValue = useMemo(() => {
    const filterValues = storedData.filters.find(storedFilter => storedFilter.name === "sharpe");
    return (filterValues?.values ?? { min, max }) as SupStoredFilterRangeValue;
  }, [min, max, storedData.filters]);

  const handleFilterChange = useCallback(
    (min: number, max: number) => {
      storedDispatch({
        type: "setFilter",
        filter: {
          name: "sharpe",
          values: {
            min: min,
            max: max,
          },
        },
      });
    },
    [storedDispatch]
  );

  const formatter = (value: number) => value.toFixed(2);

  return (
    <GridBarFiltersAtomHeader name="sharpe" title="Sharpe Range" caption={`${filterValue.min}-${filterValue.max}`} hideCount>
      <GridBarFiltersAtomRange
        fieldType="decimal"
        step={0.01}
        max={max}
        from={filterValue.min}
        to={filterValue.max}
        min={min}
        onChange={handleFilterChange}
        formatter={formatter}
      />
    </GridBarFiltersAtomHeader>
  );
}
